/**
 * drawer-context-menu.js
 */

import React from 'react';
import {
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import DropMenu from 'components/drop-menu';
import Dialog from 'components/dialog';
import Icon from 'components/icon';
import { AppMessagingContext } from 'contexts/app-messaging/app-messaging-context';
import { AppUtilityContext } from 'contexts/app-utility/app-utility-context';
import { LiveTrackingContext } from '../live-tracking/live-tracking-context';


export const DRAWER_MENU_ITEM = {
  SHARE_LOCATION: 'share-location',
  HELP: 'help'
};


export const DrawerContextMenu = (props) => {
  const { 
    items,
  } = props;
  const { getLocationPluginDebug } = React.useContext(AppMessagingContext);
  const { alertWarning } = React.useContext(AppUtilityContext);
  const [isShareLocationOpen, setIsShareLocationOpen] = React.useState(false);
  const { setIsTrackHelpOpen } = React.useContext(LiveTrackingContext);
  const menuItems = React.useMemo(() => {
    const replacements = {
      [DRAWER_MENU_ITEM.SHARE_LOCATION]: {
        id: 'share-location',
        icon: 'share',
        name: 'Share Location ...',
        onSelect: () => setIsShareLocationOpen(true)
      },
      [DRAWER_MENU_ITEM.HELP]: {
        id: 'help',
        icon: 'help',
        name: 'Help',
        onSelect: () => setIsTrackHelpOpen(true)
      },
    };
    const itemsFinal = items.map(item => Object.values(DRAWER_MENU_ITEM).includes(item)
      ? replacements[item]
      : item);
    if (process.env.REACT_APP_ENV === 'development') {
      itemsFinal.push({
        id: 'debug-info',
        icon: 'info',
        name: 'Debug Info',
        onSelect: async () => {
          const info = await getLocationPluginDebug();
          const text = JSON.stringify(info, null, '  ').replace(/\\n/g, '\r\n');
          alertWarning({ text: <pre style={{ whiteSpace: 'break-spaces', fontSize: '10px', maxHeight: '70vh' }}>{text}</pre> });
        }
      })
    }
    return itemsFinal;
  }, [items, setIsTrackHelpOpen, getLocationPluginDebug, alertWarning]);

  return (
    <>
      <DropMenu
        label={(
          <IconButton 
            sx={{ 
              height: '100%',
              width: '100%',
              backgroundColor: (theme) => theme.palette.sys.surfaceContainerHighest,
              borderRadius: 0.5,
            }}>
            <Icon type='menu' />
          </IconButton>
        )}
        items={menuItems}
      />

      <ShareDialog
        open={isShareLocationOpen}
        onClose={() => setIsShareLocationOpen(false)}
      />
    </>
  )
}


const ShareDialog = (props) => {
  const { doody, ...rest } = props;  
  const { enqueueSnackbar } = useSnackbar();
  const { currentLocation } = React.useContext(LiveTrackingContext);
  const coordinates = currentLocation
    ? `${currentLocation.latitude}, ${currentLocation.longitude}`
    : '';


  const onCopyToClipboard = async (value) => {
    if (!value) {
      return;
    }
    await navigator.clipboard.writeText(value);
    enqueueSnackbar(<Box>Copied '<b>{value}</b>' to clipboard</Box>, { variant: 'success' });
  }


  return(
    <Dialog
      title='Share Location'
      titleIcon='share'
      fullWidth
      showOk={false}
      {...rest}
    >
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        my: 3, 
      }}>        
        <Icon type='globe' sx={{ mr: 1 }} />
        <Typography variant='subtitle1'>
          {coordinates}
        </Typography>
        <IconButton 
          onClick={() => onCopyToClipboard(coordinates)}
          sx={{ 
            ml: 2, 
            backgroundColor: (theme) => theme.palette.sys.surfaceContainerHigh, 
            borderRadius: 0.5,
          }}
        >
          <Icon type='copy' fontSize='small' />
        </IconButton>
      </Box>
    </Dialog>
  )
}